/// <reference types="vite-plugin-pwa/client" />
/**
 * Service-worker registration + update seam (V1-03; lead-owned area).
 *
 * Behavior:
 * - PRODUCTION BUILD ONLY. vite.config.ts sets devOptions.enabled=false and
 *   injectRegister=false, so in dev/test the virtual module is an inert stub
 *   and no service worker ever registers (V1-00 "no SW" tests stay valid).
 * - registerType is 'prompt': a new deploy does NOT force-reload cashiers
 *   mid-sale. When an update is waiting, this module dispatches
 *   `sg:sw-update-available` on window; src/pwa/update.ts turns that into the
 *   shared update state every shell renders a bar from, and UI asks for the
 *   swap by dispatching `sg:sw-apply-update` (update.ts `requestReload`).
 * - The browser only re-checks sw.js on a full navigation, which an installed
 *   PWA never does. `onRegisteredSW` therefore hands `registration.update()`
 *   to update.ts, which calls it on a timer, on tab focus and on reconnect.
 * - `sg:sw-offline-ready` fires once when the static shell is precached.
 *
 * THE SWAP IS DRIVEN HERE, FROM THE REGISTRATION ITSELF — not through the
 * plugin's `updateServiceWorker`. That helper posts SKIP_WAITING and then
 * waits for workbox-window's `controlling` event to reload, and that event
 * depends on heuristics (was the update "external"? did the worker claim the
 * page?) that left the Reload button spinning forever on the first live
 * update (owner, 2026-10-07). `applyWaitingOrReload` reads the registration's
 * installing/waiting/active workers directly, waits for the real state
 * changes, and reloads only once the new worker is ACTIVE — at which point a
 * navigation is served by it whether or not it claimed the page.
 *
 * The service worker itself (generated by vite-plugin-pwa/workbox) precaches
 * ONLY built static assets and routes every /api path, cross-origin host,
 * version.json and non-GET request through NetworkOnly — see vite.config.ts.
 */
import { registerSW } from 'virtual:pwa-register';
import {
  APPLY_UPDATE_EVENT,
  UPDATE_AVAILABLE_EVENT,
  setServiceWorkerActivateWaiting,
  setServiceWorkerCheck,
} from './update';

/**
 * Installing = downloading every precache entry that changed, one at a time.
 * After the stamp moved out of the JS graph that is only the chunks whose code
 * changed, normally seconds — but a deploy that touches a shared chunk still
 * renames most of the bundle, and a shop's connection can be slow. Reloading
 * before the install finishes lands on the OLD build, so patience is cheaper
 * than a false "done".
 */
const INSTALL_TIMEOUT_MS = 4 * 60_000;
/** Activation is local work (cache cleanup); it does not take long. */
const ACTIVATE_TIMEOUT_MS = 15_000;

let registration: ServiceWorkerRegistration | null = null;
let applying = false;

export function setupServiceWorker(): void {
  if (typeof window === 'undefined') return;

  // Installed unconditionally: the Reload button must do something in a
  // browser with no service worker support, and that something is a reload.
  window.addEventListener(APPLY_UPDATE_EVENT, () => {
    if (applying) return;
    applying = true;
    void applyWaitingOrReload().finally(() => {
      applying = false;
    });
  });

  if (!('serviceWorker' in navigator)) return;

  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, reg) {
      registration = reg ?? null;
      setServiceWorkerCheck(reg ? () => reg.update() : null);
      setServiceWorkerActivateWaiting(reg ? activateWaitingWorker : null);
    },
    onNeedRefresh() {
      window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT));
    },
    onOfflineReady() {
      window.dispatchEvent(new CustomEvent('sg:sw-offline-ready'));
    },
    onRegisterError() {
      // Registration failure must never break the online app; no logging of
      // URLs/tokens here by design. version.json polling still detects
      // releases, and the Reload button falls back to a plain reload.
    },
  });
}

/** Order of a worker's life: installing → installed → activating → activated → redundant. */
const STATE_ORDER: ServiceWorkerState[] = [
  'parsed',
  'installing',
  'installed',
  'activating',
  'activated',
  'redundant',
];

/**
 * Resolve true once `sw` has reached `state` (or gone past it), false when it
 * became redundant first or the timeout ran out.
 */
function waitForState(
  sw: ServiceWorker,
  state: ServiceWorkerState,
  timeoutMs: number,
): Promise<boolean> {
  const target = STATE_ORDER.indexOf(state);
  const reached = () => sw.state !== 'redundant' && STATE_ORDER.indexOf(sw.state) >= target;

  return new Promise((resolve) => {
    if (reached()) return resolve(true);
    if (sw.state === 'redundant') return resolve(false);

    const finish = (ok: boolean) => {
      window.clearTimeout(timer);
      sw.removeEventListener('statechange', onChange);
      resolve(ok);
    };
    const onChange = () => {
      if (reached()) finish(true);
      else if (sw.state === 'redundant') finish(false);
    };
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    sw.addEventListener('statechange', onChange);
  });
}

/** Tell the waiting worker to take over; true once it is active. No reload. */
async function activateWaitingWorker(): Promise<boolean> {
  const reg = await currentRegistration();
  const next = reg?.waiting;
  if (!next) return false;
  const activated = waitForState(next, 'activated', ACTIVATE_TIMEOUT_MS);
  next.postMessage({ type: 'SKIP_WAITING' });
  return activated;
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration;
  if (!('serviceWorker' in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Get this tab onto the new build.
 *
 * 1. No new worker known yet (version.json spoke first): ask the browser to
 *    look for one.
 * 2. One is installing (precaching the new build): wait for it to finish.
 * 3. One is waiting: tell it to take over and wait until it is ACTIVE.
 * 4. Reload. A navigation is served by the active worker, so this lands on
 *    the new build. With clientsClaim the plugin may reload too — harmless.
 *
 * A plain reload any earlier is the trap: a page the OLD worker controls is
 * handed the OLD precached shell again, which is what "reload does nothing"
 * looked like.
 */
async function applyWaitingOrReload(): Promise<void> {
  const reg = await currentRegistration();
  if (!reg) {
    window.location.reload();
    return;
  }

  if (!reg.waiting && !reg.installing) {
    try {
      await reg.update();
    } catch {
      // Offline or mid-deploy; fall through and reload anyway.
    }
  }

  const installing = reg.installing;
  if (!reg.waiting && installing) {
    await waitForState(installing, 'installed', INSTALL_TIMEOUT_MS);
  }

  const next = reg.waiting;
  if (next) {
    const activated = waitForState(next, 'activated', ACTIVATE_TIMEOUT_MS);
    next.postMessage({ type: 'SKIP_WAITING' });
    await activated;
  }

  window.location.reload();
}
